/**
 * The settled line an approved Quick OK shows, from the approve route's own
 * response. Shared by the desktop card and the phone's approvals card
 * (FRONT-2321), so both report the same outcome in the same words.
 */
import type { ApproveResponse } from '~/hooks/mutations/use-approval-actions';

/** FRONT-2321: the settled line wherever the user's edited version was saved. */
export const EDITED_SETTLED_COPY = 'Saved your edited version.';

/**
 * FRONT-1639: what the "you decided: approved" line settles to, read from the
 * approve route's own response body — never a local constant. A DEFERRED
 * approval's tool call now genuinely runs inside this same request (or is
 * still running past the route's ~25s wait, `executed: "running"`), so the
 * card has to report what the server says actually happened rather than
 * assume the old "Saved to your memory." A LIVE-mode row's response carries
 * no `executed` field at all (`{ resolved: true }` — the route's shape from
 * before this ticket, unchanged), which is the `default` branch below and
 * keeps today's copy.
 *
 * Exported for its own unit test — same reasoning as `copyFor`: these
 * strings only exist after a click, so a static-markup render can't reach
 * them either.
 */
export function settledApprovedCopy(
  result: ApproveResponse | undefined,
  plainFallback: string,
  editedFallback = plainFallback,
): { text: string; tone: 'success' | 'destructive' | 'neutral' } {
  // FRONT-1747: the route says `revised` only when the user's edit actually
  // changed the call — an edit that changed nothing settles like a plain Yes.
  const fallback = result?.revised ? editedFallback : plainFallback;
  switch (result?.executed) {
    case 'succeeded':
      return {
        text: result.message || (result.wrote ? `Done — ${result.wrote}.` : fallback),
        tone: 'success',
      };
    case 'failed':
    case 'refused':
      return {
        text: result.message || result.error || "That didn't go through — nothing was changed.",
        tone: 'destructive',
      };
    case 'running':
      return { text: "Working on it — I'll confirm in the chat.", tone: 'neutral' };
    default:
      // FRONT-3031: the person asked not to be asked again this plan — say
      // whether that took, since a plan that had already ended adds nothing.
      if (result?.addedToPlan === true) {
        return { text: "Approved. I won't ask again about this until the plan ends.", tone: 'success' };
      }
      if (result?.addedToPlan === false) {
        return { text: "Approved this one. The plan has already ended, so I'll ask again next time.", tone: 'success' };
      }
      return { text: fallback, tone: 'success' };
  }
}

/**
 * FRONT-2653: how long a settled approval stays on screen, or `null` to keep
 * it. Six approved sandbox commands used to leave six identical
 * "Approved — going ahead now." boxes stacked under the chat, because every
 * decided row stays pinned (FRONT-1639) for the life of the chat.
 *
 * Only a line that tells the user nothing new goes away: a plain approval
 * that settles to the card's own plain fallback. The tool step in the thread
 * already shows what ran. Everything that carries information stays: a
 * failure or refusal, a server sentence about what was done, a run still
 * going (FRONT-2307), the confirmation that an EDITED version was used, and
 * a No. Decided by asking `settledApprovedCopy` itself whether it fell back,
 * so the two can never disagree about which line is the plain one.
 *
 * FRONT-3250: a late run that SUCCEEDED goes too, after a longer read
 * ({@link LATE_SUCCESS_SETTLE_MS}) — an edited late save included, so the
 * edited-version line above stays only on the live path. The server already
 * posted the same sentence into the chat as its own row, and carries the chat
 * on from there (FRONT-3036), so a pinned copy only repeated it under Eddy's
 * later answers for the life of the chat. That covers a run that answered
 * `running` and succeeded afterwards (`lateOutcome`). A late failure,
 * refusal or a run still going stays.
 */
export const QUIET_SETTLE_MS = 2500;

/** FRONT-3250: how long a late run's success line stays before it fades. */
export const LATE_SUCCESS_SETTLE_MS = 6000;

const PLAIN = '\u0000plain';
const EDITED = '\u0000edited';

export function quietSettleDelay(
  decided: 'approved' | 'denied' | null,
  result: ApproveResponse | undefined,
  lateOutcomeKind?: 'succeeded' | 'failed' | 'lost',
): number | null {
  if (decided !== 'approved') return null;
  if (result?.executed === 'succeeded') return LATE_SUCCESS_SETTLE_MS;
  if (result?.executed === 'running' && lateOutcomeKind === 'succeeded') return LATE_SUCCESS_SETTLE_MS;
  const settled = settledApprovedCopy(result, PLAIN, EDITED);
  return settled.text === PLAIN && settled.tone === 'success' ? QUIET_SETTLE_MS : null;
}
